import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMcpBridge } from '../mcp/McpBridgeProvider';

/** Message posted to the embedding host to focus a widget (embedded mode). */
const WIDGET_FOCUS_MESSAGE_TYPE = 'GUARDIAN_FOCUS_WIDGET';

/** How long a widget stays highlighted after being focused. */
const HIGHLIGHT_MS = 2000;

/** One request to focus a widget. The nonce is what makes two requests for the same widget differ. */
type WidgetFocusRequest = { widgetId: string; nonce: number };

type WidgetFocusContextValue = {
  /** Focus the widget a `widget://<id>` link points at. */
  focusWidget: (widgetId: string) => void;
  /** The pending focus request, if any — each widget reads its own state from here. */
  focusRequest: WidgetFocusRequest | null;
};

const NO_FOCUS: WidgetFocusContextValue = { focusWidget: () => {}, focusRequest: null };

const WidgetFocusContext = createContext<WidgetFocusContextValue>(NO_FOCUS);

export const useFocusWidget = (): WidgetFocusContextValue['focusWidget'] => useContext(WidgetFocusContext).focusWidget;

/**
 * The nonce of the focus request aimed at this widget, or `null` when the widget isn't focused —
 * the widget wrapper drives its own highlight from it. A changing number rather than a boolean:
 * clicking the same link twice must re-trigger the highlight, and a boolean that is already `true`
 * carries no such signal.
 */
export const useWidgetFocusNonce = (widgetId: string | undefined): number | null => {
  const { focusRequest } = useContext(WidgetFocusContext);
  return widgetId && focusRequest?.widgetId === widgetId ? focusRequest.nonce : null;
};

/** The export board copies this tree; an on-screen highlight must not be baked into the image. */
export const WithoutWidgetFocus = ({ children }: { children: ReactNode }) => (
  <WidgetFocusContext.Provider value={NO_FOCUS}>{children}</WidgetFocusContext.Provider>
);

/**
 * Resolves how a `widget://<id>` link focuses its widget, by mode:
 * - portal: the focused id goes into state here and the widget wrapper reacts to it, scrolling
 *   itself into view and animating. React owns the highlight, so a re-render cannot drop it the
 *   way an imperatively added class could.
 * - embedded (iframe): the dashboard is owned by the host, so post the widget id to the parent
 *   (via the MCP bridge, which targets the handshake origin instead of a wildcard) and let the
 *   host focus it. (Host handling is a forward-looking contract — no embedded dashboard exists
 *   yet.) If the host never handshook, fall back to the local behavior.
 */
export const WidgetFocusProvider = ({ children }: { children: ReactNode }) => {
  const { isInIframe, postToHost } = useMcpBridge();
  const [focusRequest, setFocusRequest] = useState<WidgetFocusRequest | null>(null);

  const focusWidget = useCallback(
    (widgetId: string) => {
      if (!widgetId) return;
      if (isInIframe && postToHost({ type: WIDGET_FOCUS_MESSAGE_TYPE, widgetId })) {
        return;
      }
      // Clicking the same link twice must re-trigger the highlight. Re-storing the same id is a
      // no-op React skips, so each request gets a bumped nonce — a state value that always changes.
      setFocusRequest((current) => ({ widgetId, nonce: (current?.nonce ?? 0) + 1 }));
    },
    [isInIframe, postToHost],
  );

  // The highlight is transient: drop it after a while instead of leaving it on the widget. A new
  // request replaces the object, so the countdown restarts from the latest click.
  useEffect(() => {
    if (!focusRequest) return;
    const timer = window.setTimeout(() => setFocusRequest(null), HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [focusRequest]);

  const value = useMemo(() => ({ focusWidget, focusRequest }), [focusWidget, focusRequest]);
  return <WidgetFocusContext.Provider value={value}>{children}</WidgetFocusContext.Provider>;
};
